import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

/**
 * Competition Design System — the SINGLE schedule category card: the block
 * a scheduled category occupies on a tatami timeline (board, previews,
 * future mobile/scoreboard views). Pure presentation: title, time range,
 * optional split-status badge and meta line; height/colors come from the
 * caller (the board owns the timeline geometry and the category palette).
 *
 * Extracted VERBATIM from ScheduleBoard — same class visuals, same testids
 * and data attributes, so nothing upstream (tests, e2e) changes behaviour.
 */
const useStyles = makeStyles(() => ({
    card: {
        boxSizing: 'border-box',
        borderRadius: 4,
        padding: '3px 6px',
        border: '1px solid rgba(0,0,0,0.15)',
        backgroundColor: '#e0e0e0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'default',
    },
    cardTeam: { border: '2px solid rgba(0,0,0,0.55)' },
    // A LIGHT CARD WITH A STRIPE (31 Oct 2026: «рамка … а самий фон
    // легенького тону»): the backend's tint behind dark text, the category's
    // own colour down the left edge.
    // THE AGE (31 Oct 2026): a badge in the top right, a stripe down the right.
    cardAged: {
        position: 'relative',
    },
    // The card's top right: the athletes' icon and the age badge.
    corner: {
        position: 'absolute',
        top: 3,
        right: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    ageBadge: {
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 1.3,
        padding: '1px 5px',
        borderRadius: 8,
        whiteSpace: 'nowrap',
    },
    titleWithBadge: {
        paddingRight: 38,
    },
    titleWithCorner: {
        paddingRight: 64,
    },
    cardDimmed: {
        opacity: 0.22,
    },
    // SHORT CARDS ON THE TIME AXIS (31 Oct 2026): one line, or a bar.
    cardCompact: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        padding: '0 8px',
        whiteSpace: 'nowrap',
    },
    cardBar: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: '0 6px',
        borderRadius: 3,
    },
    lineTitle: {
        fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
    },
    lineTime: { fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    barTitle: {
        fontSize: 9, fontWeight: 600, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
    },
    lineAge: {
        fontSize: 9, fontWeight: 700, padding: '0 4px', borderRadius: 6, lineHeight: 1.4, whiteSpace: 'nowrap',
    },
    cardTinted: {
        borderRadius: 6,
        padding: '4px 8px 4px 9px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    },
    cardTitle: {
        fontSize: 12, fontWeight: 600, lineHeight: 1.2,
        wordBreak: 'break-word',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    // The time before the title (the public page, 3 Nov 2026).
    inlineTime: { fontWeight: 700, marginRight: 8, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    // …in a pill of its own, so it never runs into the title (3 Nov 2026:
    // «виділимо часовий інтервал, щоб він не зливався із назвою»).
    timePill: {
        display: 'inline-block',
        padding: '0 6px',
        borderRadius: 9,
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,0,0,0.18)',
        color: '#1a1a1a',
        lineHeight: '16px',
        flex: '0 0 auto',
    },
    cardTime: { fontSize: 10, fontWeight: 600, marginTop: 1, letterSpacing: '0.2px', whiteSpace: 'nowrap' },
    cardMeta: { fontSize: 9, marginTop: 1, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    splitStatusBadge: {
        display: 'inline-block',
        background: '#ff9800',
        color: '#000',
        fontSize: 9,
        fontWeight: 700,
        padding: '1px 4px',
        borderRadius: 3,
        letterSpacing: '0.3px',
        lineHeight: 1.4,
        marginLeft: 4,
        verticalAlign: 'middle',
    },
}));

const ScheduleCard = props => {
    const {
        categoryId, title, startTime, endTime, meta,
        isTeam, splitLabel, height, backgroundColor, color,
        categoryType, durationMin, containerProps, tintColor,
        ageLabel, ageTitle, ageColor, ageTextColor, dimmed, density, athletesButton, timeFirst,
    } = props;
    const time = `${startTime} – ${endTime}`;
    const classes = useStyles();
    // Given a tint: the light card, the colour as its left stripe, dark text.
    const tinted = Boolean(tintColor && backgroundColor);
    const text = tinted ? '#1a1a1a' : color;
    const style = tinted
        ? { height, backgroundColor: tintColor, color: text, borderLeft: `5px solid ${backgroundColor}` }
        : { height, backgroundColor, color };
    if (ageColor) {
        style.borderRight = `5px solid ${ageColor}`;
    }

    return (
        <div
            className={[
                classes.card,
                tinted ? classes.cardTinted : '',
                isTeam ? classes.cardTeam : '',
                ageLabel ? classes.cardAged : '',
                dimmed ? classes.cardDimmed : '',
                'compact' === density ? classes.cardCompact : '',
                'bar' === density || 'thin' === density ? classes.cardBar : '',
            ].filter(Boolean).join(' ')}
            style={style}
            data-tinted={tinted ? 'true' : 'false'}
            data-testid='schedule-category-card'
            data-entity-id={categoryId}
            data-category-type={categoryType || undefined}
            data-is-team={isTeam ? 'true' : 'false'}
            data-is-split={splitLabel ? 'true' : 'false'}
            data-duration-min={durationMin}
            {...containerProps}
        >
            {'full' !== density && 'thin' !== density && (
                <>
                    {timeFirst && 'compact' === density && (
                        <span className={`${classes.lineTime} ${classes.timePill}`} data-testid='schedule-card-time'>
                            {time}
                        </span>
                    )}
                    <span className={'bar' === density ? classes.barTitle : classes.lineTitle} style={{ color: text }} data-testid='schedule-card-line'>
                        {title}
                    </span>
                    {!timeFirst && 'compact' === density && (
                        <span className={classes.lineTime} data-testid='schedule-card-time' style={{ color: text }}>
                            {startTime} – {endTime}
                        </span>
                    )}
                    {'compact' === density && ageLabel && (
                        <span className={classes.lineAge} style={{ backgroundColor: ageColor || '#cfd8dc', color: ageTextColor || '#1a1a1a' }} title={ageTitle || undefined} data-testid='schedule-card-age'>
                            {ageLabel}
                        </span>
                    )}
                    {athletesButton}
                </>
            )}
            {'full' === density && (ageLabel || athletesButton) && (
                <span className={classes.corner}>
                    {athletesButton}
                    {ageLabel && (
                        <span
                            className={classes.ageBadge}
                            style={{ backgroundColor: ageColor || '#cfd8dc', color: ageTextColor || '#1a1a1a' }}
                            title={ageTitle || undefined}
                            data-testid='schedule-card-age'
                        >
                            {ageLabel}
                        </span>
                    )}
                </span>
            )}
            {'full' === density && (<>
            <span className={[classes.cardTitle, athletesButton ? classes.titleWithCorner : (ageLabel ? classes.titleWithBadge : '')].filter(Boolean).join(' ')} style={{ color: text }}>
                {timeFirst && (
                    <span className={`${classes.inlineTime} ${classes.timePill}`} data-testid='schedule-card-time'>{time}</span>
                )}
                {title}
                {splitLabel && (
                    <span className={classes.splitStatusBadge} data-testid='schedule-card-split-status'>
                        {splitLabel}
                    </span>
                )}
            </span>
            {!timeFirst && (
                <div className={classes.cardTime} data-testid='schedule-card-time' style={{ color: text }}>
                    {time}
                </div>
            )}
            {meta && (
                <div className={classes.cardMeta} style={{ color: text }}>
                    {meta}
                </div>
            )}
            </>)}
        </div>
    );
};

ScheduleCard.propTypes = {
    categoryId: PropTypes.string,
    title: PropTypes.node,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    /** Pre-joined meta line (athlete count, fights, …) — null hides the row. */
    meta: PropTypes.node,
    isTeam: PropTypes.bool,
    /** Split-status label (e.g. "1/2") — presence marks the card as split. */
    splitLabel: PropTypes.node,
    height: PropTypes.number,
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    categoryType: PropTypes.string,
    durationMin: PropTypes.number,
    /** Extra DOM attributes / handlers spread onto the card container. */
    containerProps: PropTypes.object,
    /** The light background; `backgroundColor` then becomes the left stripe. */
    tintColor: PropTypes.string,
    /** The age group: «10–11» on the badge, its name on hover, its colour on the badge and the right stripe. */
    ageLabel: PropTypes.string,
    ageTitle: PropTypes.string,
    ageColor: PropTypes.string,
    ageTextColor: PropTypes.string,
    /** Faded — another age group is highlighted in the legend. */
    dimmed: PropTypes.bool,
    /** The athletes' icon (31 Oct 2026) — in the top right, or at a short card's line end. */
    athletesButton: PropTypes.node,
    /** What a card of its height holds: the whole card, one line, a bar with its title, or a bare bar. */
    density: PropTypes.oneOf(['full', 'compact', 'bar', 'thin']),
    /** The time before the title, on the card's first line (the public page, 3 Nov 2026). */
    timeFirst: PropTypes.bool,
};

ScheduleCard.defaultProps = {
    categoryId: null,
    title: null,
    startTime: null,
    endTime: null,
    meta: null,
    isTeam: false,
    splitLabel: null,
    height: null,
    backgroundColor: null,
    color: null,
    categoryType: null,
    durationMin: null,
    containerProps: null,
    tintColor: null,
    ageLabel: null,
    ageTitle: null,
    ageColor: null,
    ageTextColor: null,
    dimmed: false,
    density: 'full',
    athletesButton: null,
    timeFirst: false,
};

export default ScheduleCard;
